import { Alert, Box, Group, Tabs } from "@mantine/core";
import React from "react";
import { useHistory } from "react-router-dom";
import { useAppSelector } from "../../../hooks";
import ChecklistBoardSelector from "./ChecklistBoardSelector";
import { TypeOfChecklistEnum } from "./ChecklistConstants";
import { usePostProjectByProjectIdChecklistAllQuery } from "../../../../openapi";
import type { IChecklistPopulated } from "@mctypes/checklist.types";
import "./checklistapi.enhance";

const PROJECT_TEMPLATE_HELP_URL =
  "https://multiconsultas.sharepoint.com/sites/DSP/SitePages/Hvordan%20Dokumentkontroll%20utf%C3%B8res%20i%20CORE.aspx#opprettelse-og-bruk-av-oppdragsspesifikke-sjekklister.";

const ChecklistBoards: React.FC<{ typeOfChecklist: TypeOfChecklistEnum }> = ({ typeOfChecklist }) => {
  const project = useAppSelector((state) => state.adminReducer.project);
  const isTemplateProject = useAppSelector((state) => state.adminReducer.project?.maconomy?.projectNo === 100);
  const history = useHistory();

  const changeTypeOfChecklistInUrl = (_typeOfChecklist: TypeOfChecklistEnum) => {
    history.push(`/project/${project._id}/checklist/${_typeOfChecklist}`);
  };

  return (
    <Box w={"100%"} style={{ overflow: "hidden" }}>
      {isTemplateProject === true && (
        <Alert m="0.5rem" color="yellow" variant="light">
          Alle sjekklister i dette prosjektet vil være tilgjengelige som maler i andre prosjekter!
        </Alert>
      )}
      <Box w={"100%"} px={"xs"} pt={"xs"}>
        <Group
          w="100%"
          justify="flex-start"
          align="end"
          wrap="nowrap"
          style={{ boxShadow: "inset 0 -2px 0 0 #e4e7eb" }}
        >
          <Tabs
            value={typeOfChecklist}
            onChange={(value) =>
              changeTypeOfChecklistInUrl((value ?? TypeOfChecklistEnum.CHECKLIST) as TypeOfChecklistEnum)
            }
            styles={{ tab: { fontWeight: 600 } }}
          >
            <Tabs.List>
              <Tabs.Tab style={{ fontSize: "1.05em", fontWeight: 400 }} value={TypeOfChecklistEnum.CHECKLIST}>
                Sjekklister
              </Tabs.Tab>
              {isTemplateProject === false && (
                <Tabs.Tab style={{ fontSize: "1.05em", fontWeight: 400 }} value={TypeOfChecklistEnum.TEMPLATE}>
                  Prosjektmaler
                </Tabs.Tab>
              )}
            </Tabs.List>
          </Tabs>
        </Group>
      </Box>
      {typeOfChecklist === TypeOfChecklistEnum.CHECKLIST && <ChecklistInstanceBoardSelector />}
      {typeOfChecklist === TypeOfChecklistEnum.TEMPLATE && (
        <ChecklistTemplateBoardSelector projectTemplateHelpUrl={PROJECT_TEMPLATE_HELP_URL} />
      )}
    </Box>
  );
};

export default ChecklistBoards;

const ChecklistInstanceBoardSelector: React.FC<{}> = ({}) => {
  const project = useAppSelector((state) => state.adminReducer.project);
  const { selectedPnsFilterItem, freeTextSearch } = useAppSelector((state) => state.checklistReducer);

  const {
    data: projectChecklists = [],
    isLoading: checklistsLoading,
    isError: checklistsError,
  } = usePostProjectByProjectIdChecklistAllQuery({
    projectId: project._id,
    body: freeTextSearch ? { freeTextSearch } : {},
  });

  const templateType = project.maconomy?.projectNo === 100 ? "MC" : "";

  const filtredByPns =
    selectedPnsFilterItem != null && selectedPnsFilterItem !== ""
      ? projectChecklists.filter((checklist) =>
          checklist.pns?.toLowerCase().startsWith(selectedPnsFilterItem.toLowerCase()),
        )
      : projectChecklists;

  return (
    <ChecklistBoardSelector
      name="ChecklistInstanceBoardSelector"
      templateType={templateType}
      checklists={filtredByPns as unknown as IChecklistPopulated[]}
      loading={checklistsLoading}
      error={checklistsError}
      createNewTooltip="Start sjekkliste"
    />
  );
};

const ChecklistTemplateBoardSelector: React.FC<{ projectTemplateHelpUrl?: string }> = ({ projectTemplateHelpUrl }) => {
  const project = useAppSelector((state) => state.adminReducer.project);
  const { freeTextSearch } = useAppSelector((state) => state.checklistReducer);

  const {
    data: projectTemplates = [],
    isLoading: templatesLoading,
    isError: templatesError,
  } = usePostProjectByProjectIdChecklistAllQuery({
    projectId: project._id,
    body: { ...(freeTextSearch ? { freeTextSearch } : {}), isTemplate: true },
  });

  const templateType = project.maconomy?.projectNo === 100 ? "MC" : "PROJECT";

  return (
    <ChecklistBoardSelector
      name="ChecklistTemplateBoardSelector"
      templateType={templateType}
      checklists={projectTemplates as unknown as IChecklistPopulated[]}
      loading={templatesLoading}
      error={templatesError}
      createNewTooltip="Opprett oppdragsspesifikk sjekkliste"
      projectTemplateHelpUrl={projectTemplateHelpUrl}
    />
  );
};
